import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  Target, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  Pickaxe, Factory, Gem, Gauge, ChevronRight, Activity, BarChart3,
  ArrowUpRight, ArrowDownRight, Layers, Minus, Users, Eye
} from 'lucide-react';
import OperationsExecutiveScorecard from './OperationsExecutiveScorecard';
import ExecutiveManagementQuestions from '../../components/ExecutiveManagementQuestions';

// ============ PREMIUM DESIGN TOKENS ============
const colors = {
  text: {
    primary: '#0E1633',
    secondary: '#425070',
    tertiary: '#7A859D',
    inverse: '#FFFFFF',
  },
  bg: {
    page: '#F5F7FB',
    card: '#FFFFFF',
    dark: '#0D1538',
  },
  accent: {
    gold: '#B89A3C',
    goldBg: '#FBF8F0',
  }
};

const typography = {
  bodyText: 'font-sans text-[14px] leading-[22px] font-normal',
};

// ============ KEY READINGS DATA ============
const keyReadingsData = {
  2026: [
    {
      title: { pt: 'Produção sustenta base de receita', en: 'Production sustains revenue base' },
      insight: { pt: 'Quilates recuperados atingiram 94.4% do plano trimestral, com teor de alimentação estável a 15.74 cpht. A variabilidade foi contida pela disciplina de blending.', en: 'Recovered carats reached 94.4% of quarterly plan, with stable feed grade at 15.74 cpht. Variability was contained by blending discipline.' },
      anchor: '42.5K',
      anchorLabel: { pt: 'Quilates 1T26', en: 'Carats Q1 26' },
      type: 'positive',
      weight: 'primary'
    },
    {
      title: { pt: 'Disponibilidade da planta sob pressão', en: 'Plant availability under pressure' },
      insight: { pt: 'Paragem não planeada do Moinho SAG (72h) impactou disponibilidade para 92.3% vs 95% target. Equipa de manutenção respondeu em tempo recorde.', en: 'Unplanned SAG Mill shutdown (72h) impacted availability to 92.3% vs 95% target. Maintenance team responded in record time.' },
      anchor: '92.3%',
      anchorLabel: { pt: 'Disponibilidade', en: 'Availability' },
      type: 'attention',
      weight: 'secondary'
    },
    {
      title: { pt: 'Custo unitário acima do orçamento', en: 'Unit cost above budget' },
      insight: { pt: 'Custo de 42.5 USD/ct (+6.3% vs orç.) reflecte menor diluição de custos fixos e custos de reparação extraordinários. Foco em recuperação no 2T.', en: 'Cost of 42.5 USD/ct (+6.3% vs budget) reflects lower fixed cost dilution and extraordinary repair costs. Focus on Q2 recovery.' },
      anchor: '+6.3%',
      anchorLabel: { pt: 'vs Orçamento', en: 'vs Budget' },
      type: 'attention',
      weight: 'secondary'
    }
  ],
  2025: [
    {
      title: { pt: 'Ano de consolidação operacional', en: 'Year of operational consolidation' },
      insight: { pt: 'Produção de 178.5 Kcts (-0.8% vs plano) com taxa de recuperação de 15.94 cpht (+1.9%), demonstrando melhoria contínua no processo de concentração.', en: 'Production of 178.5 Kcts (-0.8% vs plan) with recovery rate of 15.94 cpht (+1.9%), demonstrating continuous improvement in concentration process.' },
      anchor: '178.5K',
      anchorLabel: { pt: 'Quilates 2025', en: 'Carats 2025' },
      type: 'positive',
      weight: 'primary'
    },
    {
      title: { pt: 'Eficiência de custos validada', en: 'Cost efficiency validated' },
      insight: { pt: 'Custo unitário de 38.2 USD/ct (-2.1% vs orç.) apesar de pressões inflacionárias de +8% no diesel e +12% em consumíveis de moagem.', en: 'Unit cost of 38.2 USD/ct (-2.1% vs budget) despite inflationary pressures of +8% in diesel and +12% in grinding consumables.' },
      anchor: '-2.1%',
      anchorLabel: { pt: 'Custo vs Orç.', en: 'Cost vs Bud.' },
      type: 'positive',
      weight: 'secondary'
    },
    {
      title: { pt: 'Segurança como prioridade absoluta', en: 'Safety as absolute priority' },
      insight: { pt: 'Zero fatalidades e LTIFR de 0.42 (melhor do sector em Angola). Investimento em formação e equipamentos de segurança aumentou 15% face a 2024.', en: 'Zero fatalities and LTIFR of 0.42 (industry leading in Angola). Investment in safety training and equipment increased 15% vs 2024.' },
      anchor: '0.42',
      anchorLabel: { pt: 'LTIFR', en: 'LTIFR' },
      type: 'positive',
      weight: 'secondary'
    }
  ]
};

// ============ SYNTHESIS & OUTLOOK DATA ============
const synthesisOutlookData = {
  2026: {
    synthesis: {
      pt: 'O 1T26 confirmou resiliência operacional: produção a 94% do plano com teor estável. A paragem não planeada do SAG foi contida pela resposta rápida da manutenção. Os fundamentos permanecem sólidos para recuperar o gap de volume no 2T.',
      en: 'Q1 26 confirmed operational resilience: production at 94% of plan with stable grade. The unplanned SAG shutdown was contained by rapid maintenance response. Fundamentals remain solid to recover volume gap in Q2.'
    },
    outlook: {
      focus: { pt: 'Recuperar o desvio de volume e estabilizar disponibilidade da planta.', en: 'Recover volume deviation and stabilize plant availability.' },
      priority: { pt: 'Acelerar acesso ao Bloco C2 e completar revisão preventiva do SAG.', en: 'Accelerate Block C2 access and complete SAG preventive overhaul.' },
      risk: { pt: 'Variabilidade do teor no novo bloco de minério com impacto em recuperação.', en: 'Grade variability in new ore block impacting recovery.' },
      mandate: { pt: 'Fechar o semestre com execução ≥98% em quilates recuperados.', en: 'Close the half with ≥98% execution in recovered carats.' }
    }
  },
  2025: {
    synthesis: {
      pt: '2025 validou a estratégia de optimização: taxa de recuperação acima do plano (+1.9%), custo unitário abaixo do orçamento (-2.1%) e zero fatalidades. A operação absorveu pressões externas mantendo disciplina de custos.',
      en: '2025 validated the optimization strategy: recovery rate above plan (+1.9%), unit cost below budget (-2.1%) and zero fatalities. Operations absorbed external pressures while maintaining cost discipline.'
    },
    outlook: {
      focus: { pt: 'Escalar eficiências operacionais comprovadas para 2026.', en: 'Scale proven operational efficiencies to 2026.' },
      priority: { pt: 'Expandir capacidade de processamento e elevar disponibilidade para 95%+.', en: 'Expand processing capacity and raise availability to 95%+.' },
      risk: { pt: 'Desgaste de equipamentos críticos no circuito de concentração.', en: 'Critical equipment wear in concentration circuit.' },
      mandate: { pt: 'Entregar 180K quilates com custo unitário inferior a 40 USD/ct.', en: 'Deliver 180K carats with unit cost below 40 USD/ct.' }
    }
  }
};

// ============ KEY READING CARD - Premium Executive Style (Same as Financial) ============
const OperationsKeyReadingCard = ({ reading, language }) => {
  const premiumColors = {
    bg: '#FAFBF8',
    border: '#E5EAE0',
    textPrimary: '#10205C',
    textSecondary: '#5B667A',
    accent: '#0E9F6E',
    accentAttention: '#D97706',
  };

  const accentColor = reading.type === 'attention' ? premiumColors.accentAttention : premiumColors.accent;

  return (
    <div 
      className={`relative rounded-[16px] overflow-hidden transition-all duration-300 hover:shadow-md ${reading.weight === 'primary' ? 'ring-1 ring-emerald-100' : ''}`}
      style={{ 
        backgroundColor: premiumColors.bg,
        border: `1px solid ${premiumColors.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
      }}
    >
      {/* Accent line - top border */}
      <div 
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ backgroundColor: accentColor }}
      />
      
      <div className="px-5 py-5">
        {/* Anchor Number - Dominant + Label */}
        <div className="flex items-start justify-between mb-4">
          <div>
            {/* Executive Title */}
            <h4 
              className="text-[15px] font-bold leading-snug"
              style={{ color: premiumColors.textPrimary }}
            >
              {reading.title[language]}
            </h4>
          </div>
          
          {/* Anchor Number */}
          <div className="text-right flex-shrink-0 ml-3">
            <span 
              className="text-[36px] font-extrabold leading-none tracking-tight"
              style={{ color: accentColor }}
            >
              {reading.anchor}
            </span>
            <p 
              className="text-[9px] font-semibold mt-1 uppercase tracking-wide"
              style={{ color: premiumColors.textSecondary }}
            >
              {reading.anchorLabel[language]}
            </p>
          </div>
        </div>
        
        {/* Insight Text - Concise */}
        <p 
          className="text-[12px] leading-[1.65]"
          style={{ color: premiumColors.textSecondary }}
        >
          {reading.insight[language]}
        </p>
      </div>
    </div>
  );
};

// ============ KEY READINGS SECTION ============
const OperationsKeyReadingsSection = ({ year, language }) => {
  const readings = keyReadingsData[year] || keyReadingsData[2026];
  
  return (
    <div className="space-y-6" data-testid="operations-key-readings">
      {/* Section Header - Premium */}
      <div className="flex items-center gap-3">
        <div 
          className="w-1 h-6 rounded-full"
          style={{ backgroundColor: colors.accent.gold }}
        />
        <h2 className="font-serif text-xl font-semibold" style={{ color: colors.text.primary }}>
          {language === 'pt' ? 'Leituras-Chave do Trimestre' : 'Key Quarterly Readings'}
        </h2>
      </div>
      
      {/* 3 Reading Cards Grid - Premium spacing */}
      <div className="grid grid-cols-3 gap-5">
        {readings.map((reading, idx) => (
          <OperationsKeyReadingCard key={idx} reading={reading} language={language} />
        ))}
      </div>
    </div>
  );
};

// Mock data for operations - will be moved to mockData.js later
const operationsData = {
  2026: {
    q1: {
      period: '1T 2026',
      kpis: {
        minerioExtraido: { value: 285000, plan: 300000, unit: 't', variance: -5 },
        minerioTratado: { value: 270000, plan: 280000, unit: 't', variance: -3.6 },
        quilatesRecuperados: { value: 42500, plan: 45000, unit: 'cts', variance: -5.6 },
        taxaRecuperacao: { value: 15.74, plan: 16.07, unit: 'cpht', variance: -2.1 },
        disponibilidadePlanta: { value: 92.3, plan: 95, unit: '%', variance: -2.8 },
        custoUnitario: { value: 42.5, plan: 40, unit: 'USD/ct', variance: 6.3 },
      },
      alerts: [
        { type: 'warning', area: 'Mina', message: { pt: 'Atraso no acesso ao Bloco C2 - impacto de 15kt no trimestre', en: 'Delay in Block C2 access - 15kt quarterly impact' } },
        { type: 'critical', area: 'Planta', message: { pt: 'Paragem não planeada do Moinho SAG (72h) - recuperação em curso', en: 'Unplanned SAG Mill shutdown (72h) - recovery in progress' } },
        { type: 'info', area: 'Stocks', message: { pt: 'Stock de ROM em níveis saudáveis (12 dias)', en: 'ROM stock at healthy levels (12 days)' } },
      ],
      executiveSummary: {
        pt: `O 1º Trimestre de 2026 apresentou desafios operacionais moderados, com a produção a atingir 94% do plano. O principal impacto veio da paragem não planeada do Moinho SAG durante 72 horas e atrasos no acesso ao Bloco C2.

A taxa de recuperação manteve-se ligeiramente abaixo do target (15.74 vs 16.07 cpht), refletindo a variabilidade do minério processado. A disponibilidade da planta foi afetada pela paragem do SAG, mas a equipa de manutenção respondeu rapidamente.

O custo unitário ficou 6.3% acima do orçamento, influenciado pelo menor volume de produção e custos de reparação extraordinários. Ações corretivas estão em implementação para o 2T.`,
        en: `Q1 2026 presented moderate operational challenges, with production reaching 94% of plan. The main impact came from the unplanned SAG Mill shutdown for 72 hours and delays in Block C2 access.

Recovery rate remained slightly below target (15.74 vs 16.07 cpht), reflecting variability in processed ore. Plant availability was affected by the SAG shutdown, but the maintenance team responded quickly.

Unit cost was 6.3% above budget, influenced by lower production volume and extraordinary repair costs. Corrective actions are being implemented for Q2.`
      },
    },
  },
  2025: {
    annual: {
      period: '2025',
      kpis: {
        minerioExtraido: { value: 1180000, plan: 1200000, unit: 't', variance: -1.7 },
        minerioTratado: { value: 1120000, plan: 1150000, unit: 't', variance: -2.6 },
        quilatesRecuperados: { value: 178500, plan: 180000, unit: 'cts', variance: -0.8 },
        taxaRecuperacao: { value: 15.94, plan: 15.65, unit: 'cpht', variance: 1.9 },
        disponibilidadePlanta: { value: 94.2, plan: 95, unit: '%', variance: -0.8 },
        custoUnitario: { value: 38.2, plan: 39, unit: 'USD/ct', variance: -2.1 },
      },
      conclusions: [
        { type: 'positive', message: { pt: 'Taxa de recuperação superou o plano em 1.9%', en: 'Recovery rate exceeded plan by 1.9%' } },
        { type: 'positive', message: { pt: 'Custo unitário 2.1% abaixo do orçamento', en: 'Unit cost 2.1% below budget' } },
        { type: 'neutral', message: { pt: 'Volume de produção ligeiramente abaixo do target (-0.8%)', en: 'Production volume slightly below target (-0.8%)' } },
        { type: 'info', message: { pt: 'Zero fatalidades e LTIFR em 0.42 (melhor do sector)', en: 'Zero fatalities and LTIFR at 0.42 (industry leading)' } },
      ],
      executiveSummary: {
        pt: `O ano de 2025 foi um ano de consolidação operacional para a ABC. Apesar de um arranque desafiante no 1º semestre, a empresa conseguiu recuperar no 2º semestre e terminar o ano com métricas operacionais próximas do plano.

A taxa de recuperação foi o destaque positivo, atingindo 15.94 cpht vs 15.65 cpht planeado (+1.9%), resultado de melhorias no processo de concentração e maior controlo da diluição no minério.

A disponibilidade da planta ficou ligeiramente abaixo do target (94.2% vs 95%), impactada por duas paragens não planeadas no 1T e 3T. O plano de manutenção preventiva foi revisto para 2026.

Do ponto de vista de custos, a operação entregou um custo unitário de 38.2 USD/ct, 2.1% abaixo do orçamento, demonstrando disciplina operacional apesar das pressões inflacionárias.`,
        en: `2025 was a year of operational consolidation for ABC. Despite a challenging start in H1, the company managed to recover in H2 and finish the year with operational metrics close to plan.

Recovery rate was the positive highlight, reaching 15.94 cpht vs 15.65 cpht planned (+1.9%), a result of improvements in the concentration process and better dilution control in ore.

Plant availability was slightly below target (94.2% vs 95%), impacted by two unplanned shutdowns in Q1 and Q3. The preventive maintenance plan was revised for 2026.

From a cost perspective, operations delivered a unit cost of 38.2 USD/ct, 2.1% below budget, demonstrating operational discipline despite inflationary pressures.`
      },
    },
  },
};

const OperationsExecutiveSummary = ({ year, section, flowMap, isOverview = false, setActiveSection }) => {
  const { language } = useLanguage();
  const is2026 = year === 2026 || section?.includes('2026');
  const data = is2026 ? operationsData[2026].q1 : operationsData[2025].annual;
  
  // KPI Card component
  const KPICard = ({ label, value, plan, unit, variance, icon: Icon }) => {
    const isPositive = variance >= 0;
    const isNeutral = Math.abs(variance) < 1;
    
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <Icon className="w-5 h-5 text-slate-600" />
          </div>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
            isNeutral ? 'bg-slate-100 text-slate-600' :
            isPositive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
          }`}>
            {isNeutral ? <Minus className="w-3 h-3" /> :
             isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {variance > 0 ? '+' : ''}{variance}%
          </div>
        </div>
        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</p>
        <p className="font-serif text-2xl font-semibold text-primary">
          {typeof value === 'number' ? value.toLocaleString() : value}
          <span className="text-sm text-slate-400 ml-1 font-sans">{unit}</span>
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {language === 'pt' ? 'Plano' : 'Plan'}: {typeof plan === 'number' ? plan.toLocaleString() : plan} {unit}
        </p>
      </div>
    );
  };

  // Alert Card component
  const AlertCard = ({ alert }) => {
    const typeConfig = {
      critical: { bg: 'bg-red-50', border: 'border-red-200', icon: AlertTriangle, iconColor: 'text-red-500' },
      warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500' },
      info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: Activity, iconColor: 'text-blue-500' },
    };
    const config = typeConfig[alert.type] || typeConfig.info;
    const Icon = config.icon;
    
    return (
      <div className={`${config.bg} ${config.border} border rounded-lg p-4 flex items-start gap-3`}>
        <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
        <div>
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{alert.area}</span>
          <p className="text-sm text-slate-700 mt-1">{alert.message[language]}</p>
        </div>
      </div>
    );
  };

  // Conclusion Card component (for 2025)
  const ConclusionCard = ({ conclusion }) => {
    const typeConfig = {
      positive: { icon: CheckCircle, iconColor: 'text-emerald-500', bg: 'bg-emerald-50' },
      negative: { icon: TrendingDown, iconColor: 'text-red-500', bg: 'bg-red-50' },
      neutral: { icon: Minus, iconColor: 'text-slate-500', bg: 'bg-slate-50' },
      info: { icon: Activity, iconColor: 'text-blue-500', bg: 'bg-blue-50' },
    };
    const config = typeConfig[conclusion.type] || typeConfig.neutral;
    const Icon = config.icon;
    
    return (
      <div className={`${config.bg} rounded-lg p-4 flex items-start gap-3`}>
        <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
        <p className="text-sm text-slate-700">{conclusion.message[language]}</p>
      </div>
    );
  };

  // Title based on whether it's overview or specific section
  const getTitle = () => {
    if (isOverview) {
      return language === 'pt' ? 'Execução Operacional' : 'Operational Execution';
    }
    return language === 'pt' ? 'Resumo Executivo' : 'Executive Summary';
  };

  const getPeriodLabel = () => {
    return is2026 ? '1T 2026' : '2025';
  };

  const getSubtitle = () => {
    if (isOverview) {
      return is2026 
        ? (language === 'pt' ? 'vs Orçamento 1T 2026 e comparativo com 1T25' : 'vs Budget Q1 2026 and comparison with Q125')
        : (language === 'pt' ? 'Análise consolidada do ano fiscal' : 'Consolidated analysis of fiscal year');
    }
    return null;
  };

  return (
    <div className="space-y-8" data-testid="operations-executive-summary">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-primary">
            {getTitle()} {isOverview && <span className="text-gold">| {getPeriodLabel()}</span>}
          </h1>
          {getSubtitle() && (
            <p className="text-slate-500 mt-1">
              {getSubtitle()}
            </p>
          )}
        </div>
      </div>

      {/* FlowMap below title */}
      {flowMap && (
        <div className="mb-2">
          {flowMap}
        </div>
      )}

      {/* Executive Scorecard - Only on overview pages */}
      {isOverview && (
        <OperationsExecutiveScorecard year={is2026 ? 2026 : 2025} />
      )}

      {/* ============ LEITURAS-CHAVE DO TRIMESTRE ============ */}
      {isOverview && (
        <OperationsKeyReadingsSection year={is2026 ? 2026 : 2025} language={language} />
      )}

      {/* ============ AS 3 PERGUNTAS DE GESTÃO ============ */}
      {isOverview && (
        <ExecutiveManagementQuestions year={is2026 ? 2026 : 2025} />
      )}

      {/* ============ SÍNTESE E OUTLOOK — FECHO EXECUTIVO ============ */}
      {isOverview && (
        <div className="grid grid-cols-2 gap-8" data-testid="operations-synthesis-outlook">
          
          {/* BLOCO ESQUERDO — Síntese Institucional */}
          <div 
            className="rounded-2xl px-8 py-7 flex flex-col"
            style={{ 
              backgroundColor: colors.bg.dark,
              boxShadow: '0 4px 20px rgba(13, 21, 56, 0.12)'
            }}
          >
            {/* Header */}
            <div className="flex items-center gap-2.5 mb-6">
              <div 
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
              >
                <Users className="w-4 h-4 text-white/70" />
              </div>
              <h3 className="text-[11px] font-bold text-white/50 uppercase tracking-[0.08em]">
                {language === 'pt' 
                  ? `Síntese Operacional ${is2026 ? 'do 1T26' : 'de 2025'} à Gerência`
                  : `Operational ${is2026 ? 'Q1 26' : '2025'} Summary to Management`
                }
              </h3>
            </div>
            
            {/* Texto de Síntese — premium breathing */}
            <p 
              className="text-[15px] font-normal text-white/90 flex-1"
              style={{ 
                lineHeight: '1.85',
                letterSpacing: '0.01em'
              }}
            >
              {synthesisOutlookData[is2026 ? 2026 : 2025].synthesis[language]}
            </p>
          </div>

          {/* BLOCO DIREITO — Outlook Executivo */}
          <div 
            className="rounded-2xl px-8 py-7 flex flex-col"
            style={{ 
              backgroundColor: colors.accent.goldBg, 
              border: `1px solid rgba(184, 154, 60, 0.15)`,
              boxShadow: '0 2px 12px rgba(184, 154, 60, 0.06)'
            }}
          >
            {/* Header */}
            <div className="flex items-center gap-2.5 mb-6">
              <div 
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(184, 154, 60, 0.12)' }}
              >
                <Eye className="w-4 h-4" style={{ color: colors.accent.gold }} />
              </div>
              <h3 
                className="text-[11px] font-bold uppercase tracking-[0.08em]" 
                style={{ color: colors.accent.gold }}
              >
                Outlook {is2026 ? '2T26' : '2026'}
              </h3>
            </div>
            
            {/* Campos estruturados com hierarquia forte */}
            <div className="space-y-5 flex-1">
              {/* Foco */}
              <div>
                <span 
                  className="text-[9px] font-bold uppercase tracking-[0.1em] block mb-1"
                  style={{ color: '#8B9AAF' }}
                >
                  {language === 'pt' ? 'Foco' : 'Focus'}
                </span>
                <p className="text-[13px] text-slate-700 font-medium leading-snug">
                  {synthesisOutlookData[is2026 ? 2026 : 2025].outlook.focus[language]}
                </p>
              </div>
              
              {/* Prioridade */}
              <div>
                <span 
                  className="text-[9px] font-bold uppercase tracking-[0.1em] block mb-1"
                  style={{ color: '#8B9AAF' }}
                >
                  {language === 'pt' ? 'Prioridade' : 'Priority'}
                </span>
                <p className="text-[13px] text-slate-700 leading-snug">
                  {synthesisOutlookData[is2026 ? 2026 : 2025].outlook.priority[language]}
                </p>
              </div>
              
              {/* Risco Principal */}
              <div>
                <span 
                  className="text-[9px] font-bold uppercase tracking-[0.1em] block mb-1"
                  style={{ color: '#8B9AAF' }}
                >
                  {language === 'pt' ? 'Risco Principal' : 'Main Risk'}
                </span>
                <p className="text-[13px] text-slate-600 leading-snug">
                  {synthesisOutlookData[is2026 ? 2026 : 2025].outlook.risk[language]}
                </p>
              </div>
            </div>
            
            {/* MANDATO — Callout Executivo com destaque premium */}
            <div 
              className="mt-5 pt-5 border-t"
              style={{ borderColor: 'rgba(184, 154, 60, 0.2)' }}
            >
              <span 
                className="text-[9px] font-bold uppercase tracking-[0.1em] block mb-2"
                style={{ color: colors.accent.gold }}
              >
                {language === 'pt' ? 'Mandato de Gestão' : 'Management Mandate'}
              </span>
              <div 
                className="rounded-lg px-4 py-3"
                style={{ 
                  backgroundColor: 'rgba(14, 22, 51, 0.04)',
                  borderLeft: `3px solid ${colors.accent.gold}`
                }}
              >
                <p 
                  className="text-[14px] font-semibold leading-relaxed"
                  style={{ color: colors.text.primary }}
                >
                  {synthesisOutlookData[is2026 ? 2026 : 2025].outlook.mandate[language]}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPIs Grid - Hidden on overview pages */}
      {!isOverview && (
        <div>
          <h2 className="font-serif text-xl font-bold text-primary mb-4">
            {language === 'pt' ? 'KPIs Operacionais' : 'Operational KPIs'}
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <KPICard 
              label={language === 'pt' ? 'Minério Extraído' : 'Ore Extracted'} 
              value={data.kpis.minerioExtraido.value} 
              plan={data.kpis.minerioExtraido.plan}
              unit={data.kpis.minerioExtraido.unit}
              variance={data.kpis.minerioExtraido.variance}
              icon={Pickaxe}
            />
            <KPICard 
              label={language === 'pt' ? 'Minério Tratado' : 'Ore Processed'} 
              value={data.kpis.minerioTratado.value} 
              plan={data.kpis.minerioTratado.plan}
              unit={data.kpis.minerioTratado.unit}
              variance={data.kpis.minerioTratado.variance}
              icon={Factory}
            />
            <KPICard 
              label={language === 'pt' ? 'Quilates Recuperados' : 'Carats Recovered'} 
              value={data.kpis.quilatesRecuperados.value} 
              plan={data.kpis.quilatesRecuperados.plan}
              unit={data.kpis.quilatesRecuperados.unit}
              variance={data.kpis.quilatesRecuperados.variance}
              icon={Gem}
            />
            <KPICard 
              label={language === 'pt' ? 'Taxa de Recuperação' : 'Recovery Rate'} 
              value={data.kpis.taxaRecuperacao.value} 
              plan={data.kpis.taxaRecuperacao.plan}
              unit={data.kpis.taxaRecuperacao.unit}
              variance={data.kpis.taxaRecuperacao.variance}
              icon={Target}
            />
            <KPICard 
              label={language === 'pt' ? 'Disponibilidade Planta' : 'Plant Availability'} 
              value={data.kpis.disponibilidadePlanta.value} 
              plan={data.kpis.disponibilidadePlanta.plan}
              unit={data.kpis.disponibilidadePlanta.unit}
              variance={data.kpis.disponibilidadePlanta.variance}
              icon={Gauge}
            />
            <KPICard 
              label={language === 'pt' ? 'Custo Unitário' : 'Unit Cost'} 
              value={data.kpis.custoUnitario.value} 
              plan={data.kpis.custoUnitario.plan}
              unit={data.kpis.custoUnitario.unit}
              variance={data.kpis.custoUnitario.variance}
              icon={BarChart3}
            />
          </div>
        </div>
      )}

      {/* Executive Summary Text - Hidden on overview pages */}
      {!isOverview && (
        <div className="bg-gradient-to-br from-[#0A192F] to-[#1a2744] rounded-2xl p-8">
          <h2 className="font-serif text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-gold-400" />
            {language === 'pt' ? 'Análise Executiva' : 'Executive Analysis'}
          </h2>
          <div className="text-slate-300 leading-relaxed whitespace-pre-line">
            {data.executiveSummary[language]}
          </div>
        </div>
      )}

      {/* Alerts (2026) - Hidden on overview pages */}
      {!isOverview && is2026 && data.alerts && (
        <div>
          <h2 className="font-serif text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            {language === 'pt' ? 'Alertas e Riscos' : 'Alerts & Risks'}
          </h2>
          <div className="space-y-3">
            {data.alerts.map((alert, idx) => (
              <AlertCard key={idx} alert={alert} />
            ))}
          </div>
        </div>
      )}

      {/* Conclusions (2025) - Hidden on overview pages */}
      {!isOverview && !is2026 && data.conclusions && (
        <div>
          <h2 className="font-serif text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            {language === 'pt' ? 'Principais Conclusões' : 'Key Conclusions'}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {data.conclusions.map((conclusion, idx) => (
              <ConclusionCard key={idx} conclusion={conclusion} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationsExecutiveSummary;
